import { ClientType, ListenerFullRow } from './CourseEnrollment.const';
import { ClientForm } from './CourseEnrollment.types';

export const formatPassportDate = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length > 4) {
    return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
  }
  if (digits.length > 2) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }
  return digits;
};

export const isValidPassportDate = (val: string): boolean => {
  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return false;
  const [d, m, y] = val.split('.').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

/**
 * ДД.ММ.ГГГГ (what the form holds) → ГГГГ-ММ-ДД (what the DATEONLY API columns
 * expect). Returns null for anything that isn't a complete, real date — sending
 * the display string through made the backend fall back to `new Date(...)`,
 * which silently swapped day/month for days <= 12 and hard-failed above that.
 */
export const displayDateToIso = (val: string): string | null => {
  if (!isValidPassportDate(val)) return null;
  const [d, m, y] = val.split('.');
  return `${y}-${m}-${d}`;
};

export const isValidEmail = (val: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

/**
 * «Добавить данные из договора» — переносит данные клиента с шага 1 в форму
 * слушателя. Поля различаются по типу клиента: у физлица ФИО лежит в
 * individualFullName и есть паспортные данные, у юрлица заполнен только
 * signerName, у ИП отдельного ФИО в форме нет вовсе (наименование вида
 * «Индивидуальный предприниматель ...» подставлять в ФИО слушателя нельзя).
 */
export const buildListenerPrefillFromContract = (
  clientType: ClientType,
  form: ClientForm,
): Partial<ListenerFullRow> => {
  const externalEmail = form.email || '';

  if (clientType === 'individual') {
    return {
      fullNameRu: form.individualFullName || '',
      address: form.postalAddress || '',
      passport: form.passportNumber || '',
      passportIssueDate: form.passportIssueDate || '',
      passportIssuer: form.passportIssuer || '',
      externalEmail,
    };
  }

  if (clientType === 'ip') {
    return { address: form.postalAddress || '', externalEmail };
  }

  return {
    fullNameRu: form.signerName || '',
    address: (form.postalDiffers ? form.postalAddress : form.legalAddress) || '',
    externalEmail,
  };
};

export interface PrevRequisitesOption {
  value: string;
  /** Organisation name / ФИО — the first line of the dropdown entry. */
  name: string;
  /** «УНП 191234567» or «Паспорт MP1234567» — the second line. */
  subtitle: string;
  /** Drives the collapsed value and the search match, so it carries both. */
  label: string;
  /** Tab this snapshot belongs to; the picker shows only the active one. */
  clientType: ClientType;
  clientData: any;
}

const API_CLIENT_TYPE: Record<string, ClientType> = {
  legal_entity: 'legal',
  sole_proprietor: 'ip',
  individual: 'individual',
};

/**
 * Shapes `GET /api/clients/previous` rows into picker options.
 *
 * The identifier lives in `label` because two ООО can share a trading name and
 * only the УНП tells them apart — and `label` is what the Select shows once
 * collapsed. Callers filter the result by the active `clientType`: the form
 * fields differ per type (УНП + банковские реквизиты vs паспорт), so offering a
 * saved физлицо while the «Юр. лицо» tab is open would only half-fill the form.
 */
export const buildPrevRequisitesOptions = (items: any): PrevRequisitesOption[] =>
  (Array.isArray(items) ? items : []).map((cd) => {
    const name = cd.vnaimp || cd.fioRu || 'Клиент';
    const subtitle = cd.unp
      ? `УНП ${cd.unp}`
      : cd.passportNumber
        ? `Паспорт ${cd.passportNumber}`
        : '';
    const clientType = API_CLIENT_TYPE[cd.clientType] ?? 'legal';
    return {
      value: cd.id,
      name,
      subtitle,
      label: subtitle ? `${name} — ${subtitle}` : name,
      clientType,
      clientData: { ...cd, _clientType: clientType },
    };
  });
